import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedPages from '@/components/base/RelatedPages';

const advantages = [
  {
    icon: 'ri-leaf-line',
    tag: '01',
    title: '堅持自然選材',
    subtitle: '選用天然食物來源營養，攝取健康零負擔',
    desc: '康永生技嚴格挑選天然食物來源的原料，避免過度精製或合成添加物，讓每一份配方都回歸自然本質。從原料產地到成分純度，全程透明公開，讓消費者吃得安心，品牌賣得有底氣。',
    points: [
      '嚴格審核天然原料供應商資格',
      '每批原料附農藥殘留、重金屬檢驗報告',
      '拒絕不必要的合成添加物',
      '可依需求選用有機認證原料',
    ],
    img: 'https://readdy.ai/api/search-image?query=fresh%20natural%20herbs%20botanical%20ingredients%20turmeric%20ginger%20green%20tea%20extract%20dried%20herbs%20arranged%20beautifully%20on%20white%20marble%20surface%2C%20health%20supplement%20natural%20raw%20materials%2C%20clean%20minimal%20professional%20photography%20with%20soft%20natural%20lighting&width=720&height=480&seq=ca-natural-01&orientation=landscape',
    reverse: false,
  },
  {
    icon: 'ri-heart-line',
    tag: '02',
    title: '創造健康三贏',
    subtitle: '協助客戶輕鬆進入保健市場，幫助消費者輕鬆養出健康',
    desc: '我們相信好的代工夥伴能創造三方共贏：品牌客戶成功上市獲利、終端消費者獲得真正有效的保健產品、康永生技持續精進專業技術。這三者互為依存，缺一不可。',
    points: [
      '品牌客戶：降低進入保健市場的門檻與風險',
      '消費者：獲得成分透明、品質穩定的保健品',
      '康永：透過每次合作累積技術與口碑',
      '長期合作關係，共同成長共享成果',
    ],
    img: 'https://readdy.ai/api/search-image?query=happy%20healthy%20diverse%20group%20of%20people%20holding%20supplement%20products%20with%20green%20nature%20background%2C%20health%20wellness%20teamwork%20collaboration%20concept%2C%20warm%20natural%20lighting%20professional%20photography&width=720&height=480&seq=ca-winwin-01&orientation=landscape',
    reverse: true,
  },
  {
    icon: 'ri-service-line',
    tag: '03',
    title: '整合式專業服務',
    subtitle: '簡單六步驟實現您的理想產品，為您打造低風險天然保健品',
    desc: '從初次諮詢到產品上市，康永提供一條龍的整合式服務。您不需要分別對接多家廠商，一個窗口就能完成配方研發、生產製造、品管驗證與包裝設計，大幅降低溝通成本與風險。',
    points: [
      '第一步：需求諮詢與產品定位分析',
      '第二步：配方研發與原料選定',
      '第三步：小批試產與樣品確認',
      '第四步：品管驗收與法規審查',
      '第五步：量產製造與包裝出貨',
      '第六步：上市後技術支援',
    ],
    img: 'https://readdy.ai/api/search-image?query=professional%20biotech%20team%20meeting%20discussing%20product%20development%20roadmap%20with%20tablet%20laptop%20in%20modern%20clean%20office%2C%20health%20supplement%20company%20collaboration%2C%20natural%20light%20bright%20interior&width=720&height=480&seq=ca-service-01&orientation=landscape',
    reverse: false,
  },
  {
    icon: 'ri-store-line',
    tag: '04',
    title: '少量可接單',
    subtitle: '為您減少庫存壓力，小眾客群的最佳選擇',
    desc: '許多新品牌或小眾市場客戶面臨的最大障礙，就是最低起訂量太高。康永生技支援少量接單，讓您以最小庫存風險驗證市場需求，確認產品可行性後再放大量產規模，穩步成長。',
    points: [
      '膠囊 / 錠劑類最低 3,000 瓶起訂',
      '粉包 / 顆粒類最低 5,000 包起訂',
      '適合新品牌測試市場、小眾利基品項',
      '試產通過後可無縫銜接大量生產',
    ],
    img: 'https://readdy.ai/api/search-image?query=small%20batch%20supplement%20production%20sample%20bottles%20capsules%20arranged%20neatly%20on%20clean%20white%20table%2C%20minimal%20order%20quantity%20product%20testing%2C%20health%20product%20sampling%20photography&width=720&height=480&seq=ca-moq-01&orientation=landscape',
    reverse: true,
  },
];

export default function CoreAdvantagesContent() {
  return (
    <>
      {/* Hero Banner */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=lush%20green%20botanical%20garden%20overhead%20view%20with%20diverse%20natural%20plants%20herbs%20growing%2C%20vibrant%20emerald%20green%20nature%20background%2C%20health%20supplement%20brand%20identity%2C%20serene%20clean%20natural%20environment&width=1920&height=420&seq=ca-hero-bg-01&orientation=landscape"
            alt="核心優勢背景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/88 via-[#1a3a1a]/65 to-[#1a3a1a]/25" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb
              items={[
                { label: '首頁', href: '/' },
                { label: '核心理念' },
              ]}
              className="mb-4 text-gray-300"
            />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">
              首頁 · 核心理念
            </span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">
              核心理念
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              以天然選材、健康三贏、整合式專業服務與少量可接單，協助品牌穩健進入保健食品市場。
            </p>
          </div>
        </div>
      </section>

      {/* Advantage blocks */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-14 md:space-y-24">
          {advantages.map((adv) => (
            <div
              key={adv.tag}
              className={`flex flex-col ${adv.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`}
            >
              {/* Text */}
              <div className="w-full lg:w-1/2">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-[#4CAF50] font-bold text-[2rem] leading-none opacity-20">{adv.tag}</span>
                  <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl border border-green-200">
                    <i className={`${adv.icon} text-[#4CAF50] text-xl`} />
                  </div>
                </div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] leading-tight mb-2">{adv.title}</h2>
                <p className="text-[#4CAF50] font-medium text-[1rem] mb-5">{adv.subtitle}</p>
                <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{adv.desc}</p>
                <ul className="space-y-3">
                  {adv.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                      </div>
                      <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={adv.img}
                    alt={adv.title}
                    className="w-full h-[300px] lg:h-[360px] object-cover object-top"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
            開始合作
          </span>
          <h2 className="text-[#1a2e1a] font-bold text-[1.875rem] md:text-[2.25rem] leading-tight mb-4">
            想讓這些優勢為您所用？
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            歡迎聯絡康永生技業務團隊，我們將為您量身規劃最合適的 ODM 合作方案。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact/form"
              className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              洽詢 ODM 合作
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/cooperation/process"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer"
            >
              了解合作流程
            </Link>
          </div>
        </div>
      </section>
      <RelatedPages items={[
        { title: '專業生產', desc: '康永國際認證生產廠房，為品牌提共最好的安全生產保障。', href: '/about/production' },
        { title: '成功案例', desc: '多元品類全程代工案例，看看康永如何協助品牌成功上市。', href: '/about/cases' },
        { title: '合作流程', desc: '從需求對焦到交貨，清楚掌握合作的每個步驟。', href: '/cooperation/process' },
      ]} />
    </>
  );
}
